<template>
  <div :class="['card-grid-container', `columns-${columns}`]">
    <div
      v-for="(card, index) in cards"
      :key="index"
      :class="['feature-card', card.highlighted ? 'highlighted' : '']"
    >
      <div v-if="card.icon" class="card-icon">
        <i :class="card.icon"></i>
      </div>
      <div v-if="card.image" class="card-image">
        <img :src="card.image" :alt="card.title" />
      </div>
      <h3 class="card-title">{{ card.title }}</h3>
      <p v-if="card.description" class="card-description">{{ card.description }}</p>
      <div class="card-content" v-html="card.content"></div>
      <a
        v-if="card.link"
        :href="card.link.url"
        :target="card.link.newTab ? '_blank' : '_self'"
        class="card-link"
      >
        {{ card.link.text }}
        <i class="fas fa-arrow-right"></i>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
interface CardLink {
  url: string
  text: string
  newTab?: boolean
}

interface Card {
  title: string
  description?: string
  icon?: string
  image?: string
  content?: string
  link?: CardLink
  highlighted?: boolean
}

defineProps<{
  cards: Card[]
  columns?: number
}>()
</script>

<style scoped>
.card-grid-container {
  display: grid;
  gap: 24px;
  margin: 24px 0;
}

.card-grid-container.columns-2 {
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.card-grid-container.columns-3 {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.card-grid-container.columns-4 {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

/* Default to 3 columns if not specified */
.card-grid-container {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

.feature-card {
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(139, 92, 246, 0.1);
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #3b82f6, #2563eb, #1a1a1a);
  transform: scaleX(0);
  transition: transform 0.4s ease;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 28px rgba(139, 92, 246, 0.25);
  border-color: rgba(139, 92, 246, 0.3);
}

.feature-card:hover::before {
  transform: scaleX(1);
}

.feature-card.highlighted {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(168, 85, 247, 0.05));
  border-color: rgba(139, 92, 246, 0.3);
}

.feature-card.highlighted::before {
  transform: scaleX(1);
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.card-icon i {
  font-size: 26px;
  color: white;
}

.card-image {
  width: 100%;
  height: 180px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
}

.card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.feature-card:hover .card-image img {
  transform: scale(1.05);
}

.card-title {
  font-size: 20px;
  font-weight: 700;
  color: #1E293B;
  margin: 0 0 12px 0;
  line-height: 1.3;
}

.card-description {
  font-size: 14px;
  color: #64748B;
  margin: 0 0 16px 0;
  line-height: 1.6;
}

.card-content {
  font-size: 14px;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 16px;
}

.card-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #3b82f6;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-top: 12px;
}

.card-link:hover {
  color: #3b82f6;
  gap: 12px;
}

.card-link i {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.card-link:hover i {
  transform: translateX(4px);
}

@media (max-width: 768px) {
  .card-grid-container {
    grid-template-columns: 1fr;
  }
}
</style>
